import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { MfaEnrollment } from '@/components/auth/MfaEnrollment';
import { ContentSkeleton } from './ContentSkeleton';

/**
 * MFA enforcement gate for internal (IDSeat staff) admin pages. Internal-admin
 * routes are unreachable until the signed-in idseat_admin has a verified TOTP
 * factor: with zero factors we render the enrollment panel in place of the page
 * content (the sidebar/topbar stay visible). Club admins are never gated.
 *
 * Two rules, learned on staging (2026-09-22):
 *  - A factor list that could not be READ is not "no factor". listFactors()
 *    goes through GET /auth/v1/user, which fails once the session has been
 *    revoked server-side while the browser still holds a valid-looking token.
 *    Treating that as zero showed the enrolment panel to an admin whose
 *    authenticator was enabled, with nothing to do but "Remove".
 *  - The answer belongs to a SESSION, not a user. Keyed by user id alone, the
 *    cache kept a stale zero for five minutes across a re-login in the same
 *    tab. The access token changes on login and on the TOTP step-up, so it is
 *    part of the key.
 */
export function InternalAdminMfaGate({ children }: { children: React.ReactNode }) {
  const { user, session, isIdseatAdmin, signOut } = useAuth();
  const location = useLocation();
  const isInternalAdmin = location.pathname.startsWith('/admin');
  const enabled = isIdseatAdmin && isInternalAdmin && !!user?.id;

  const { data: verifiedCount, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['mfa-factors', user?.id, session?.access_token ?? null],
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      return data?.totp?.length ?? 0;
    },
  });

  // Not an internal admin (or not on /admin): never gate.
  if (!enabled) return <>{children}</>;
  // Resolving factor state — hold content rather than flashing the page.
  if (isLoading) return <ContentSkeleton />;

  if (isError) {
    return (
      <div className="w-full max-w-2xl mx-auto space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-[12px] bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-[18px] font-bold text-foreground">Couldn't confirm your two-factor status</h1>
            <p className="text-[14px] text-muted-foreground">
              Your session may have expired. Try again, or sign in again to continue.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            Try again
          </Button>
          <Button onClick={() => signOut()}>Sign in again</Button>
        </div>
      </div>
    );
  }

  if ((verifiedCount ?? 0) > 0) return <>{children}</>;

  return (
    <div className="w-full max-w-2xl mx-auto space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-[12px] bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <ShieldAlert className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <h1 className="text-[18px] font-bold text-foreground">Two-factor authentication required</h1>
          <p className="text-[14px] text-muted-foreground">
            Internal admin access requires two-factor authentication. Set it up below to continue.
          </p>
        </div>
      </div>
      <MfaEnrollment onEnrolled={() => refetch()} />
    </div>
  );
}
